import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useNavigate, useLocation } from "react-router-dom";
import Translate from "../../../utils/Translate";
import { useApi } from "../../../utils/Api";
import Preloader from "../../Preloader";
import { Box, Chip, Dialog, DialogTitle, Grid, Typography } from "@mui/material";
import { formatDate } from "../../../utils/dateTimeFormat";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import EditIcon from "@mui/icons-material/Edit";
import DotMenu from "../../DotMenu";
import AddOrUpdateMarketNote from "./AddOrUpdateMarketNote";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";

const MarketNotes = ({ actor, type }) => {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const closeAndReload = () => {
    const loc = encodeURIComponent(location.pathname);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [marketNotes, setMarketNotes] = useState([]);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedMarketNote, setSelectedMarketNote] = useState(null);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  useEffect(() => {
    async function fetchData() {
      await getMarketNotes();
    }
    fetchData();
  }, []);

  async function getMarketNotes() {
    setIsLoading(true);
    let basePath;
    if (type === "person") {
      basePath = "customerrelations/contactperson";
    } else {
      basePath = "customerrelations/company";
    }

    try {
      let response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}${basePath}/marketnotes/${actor.actorId}`,
        false,
        "GET",
      );
      if (response.isSuccessful) {
        const sortedNotes = response.marketNotes.sort((a, b) => {
          return new Date(b.regDate) - new Date(a.regDate);
        });
        setMarketNotes(sortedNotes);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const getDotMenuItems = (note) => {
    const menudata = [
      {
        icon: <EditIcon />,
        text: Translate.get("Alter"),
        onClick: () => handleOpenEdit(note),
      },
      {
        icon: <CheckIcon />,
        text: Translate.get("ClearMark"),
        disabled: note.isDone === true,
        onClick: () => {
          clearMarkNote(note);
        },
      },
      {
        icon: <DeleteIcon />,
        text: Translate.get("Delete"),
        onClick: () => {
          setNoteToDelete(note);
          setIsDeleteConfirmOpen(true);
        },
      },
    ];
    return menudata;
  };

  function handleOpenEdit(note) {
    setSelectedMarketNote(note);
    setIsEditOpen(true);
  }

  function handleCloseEdit() {
    setIsEditOpen(false);
    setSelectedMarketNote(null);
  }

  async function handleEditSuccess() {
    setIsEditOpen(false);
    setSelectedMarketNote(null);

    await getMarketNotes();
  }

  function closeDeleteDialog() {
    setIsDeleteConfirmOpen(false);
    setNoteToDelete(null);
  }

  async function handleConfirmDelete() {
    const note = noteToDelete;
    closeDeleteDialog();
    await deleteMarketNote(note);
  }

  async function deleteMarketNote(note) {
    setIsLoading(true);

    try {
      let response = await api.fetch(
        `${import.meta.env.VITE_MAIN_URL}customerrelations/marketnote/${note.marketNoteId}`,
        true,
        "DELETE",
      );
      if (response.isSuccessful) {
        await getMarketNotes();
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function clearMarkNote(note) {
    setIsLoading(true);

    try {
      let reqObj = {
        marketNoteId: note.marketNoteId,
        text: note.textNote,
        isDone: true,
        notificationDate: note.notificationDate,
        companyActorId: note.companyActorId,
        contactPersonActorId: note.contactPersonActorId,
        responsibleEPortUserId: note.responsibleEPortUserId,
        marketNoteLabelIds: note.marketNoteLabels
          ? note.marketNoteLabels.map((label) => label.marketNoteLabelId)
          : null,
      };

      let response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}customerrelations/marketnote`, reqObj, "PUT");
    } finally {
      setIsLoading(false);
      await getMarketNotes();
    }
  }

  return (
    <>
      {isLoading && <Preloader />}
      <DialogContent dividers={true}>
        {marketNotes.map((note, index) => (
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "5px",
              marginTop: "1em",
              padding: "1em",
            }}
            key={index}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {Translate.get("Created")}: {note.regDate ? formatDate(note.regDate) : ""}
                </Typography>

                <div>
                  {note.isDone ? (
                    <CheckIcon sx={{ color: "success.main" }} />
                  ) : (
                    <HourglassTopIcon sx={{ color: "text.disabled" }} />
                  )}
                </div>
              </Box>

              <DotMenu content={getDotMenuItems(note)} />
            </Box>

            <div style={{ marginBottom: "1em" }}>
              {note.marketNoteLabels.map((marketNoteLabel, index) => (
                <Chip
                  key={marketNoteLabel.marketNoteLabelId}
                  label={marketNoteLabel.labelText}
                  style={{ marginRight: "0.2em", marginTop: "0.2em" }}
                  variant="outlined"
                ></Chip>
              ))}
            </div>

            <Grid container spacing={2}>
              <Grid size={{ md: 12, xs: 12 }} style={{ marginBottom: "2em" }}>
                <Typography variant="caption" color="text.secondary">
                  {Translate.get("Note")}
                </Typography>
                <Typography variant="body1">{note.textNote}</Typography>
              </Grid>

              <Grid size={{ md: 6, xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {Translate.get("Company")}
                </Typography>
                <Typography variant="body1">{note.companyActorName}</Typography>
              </Grid>

              <Grid size={{ md: 6, xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {Translate.get("Contact")}
                </Typography>
                <Typography variant="body1">{note.contactPersonActorName}</Typography>
              </Grid>

              <Grid size={{ md: 6, xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {Translate.get("Responsible")}
                </Typography>
                <Typography variant="body1">{note.responsibleEPortUserName}</Typography>
              </Grid>

              <Grid size={{ md: 6, xs: 6 }}>
                <Typography variant="caption" color="text.secondary">
                  {Translate.get("NotificationDate")}
                </Typography>
                <Typography variant="body1">
                  {note.notificationDate ? formatDate(note.notificationDate) : ""}
                </Typography>
              </Grid>
            </Grid>
          </div>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeAndReload()}>{Translate.get("Close")}</Button>
        <Button onClick={() => setIsEditOpen(true)} variant="contained">
          {Translate.get("Add")}
        </Button>
      </DialogActions>

      <Dialog open={isEditOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <AddOrUpdateMarketNote
          actor={actor}
          type={type}
          existingMarketNote={selectedMarketNote}
          onClose={handleCloseEdit}
          onSuccess={handleEditSuccess}
        />
      </Dialog>

      <Dialog open={isDeleteConfirmOpen} onClose={closeDeleteDialog}>
        <DialogTitle>{Translate.get("ConfirmDeletion")}</DialogTitle>
        <DialogContent>
          <Typography>{Translate.get("SureDeleteMarketNote")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>{Translate.get("Cancel")}</Button>
          <Button onClick={handleConfirmDelete} variant="contained">
            {Translate.get("Delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default MarketNotes;
